/**
 * API Client
 * Axios instance configured with interceptors for auth and error handling
 */

import axios, { AxiosError } from "axios";
import type { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { getConfigSync } from "../config";

// Token provider function type
type TokenProvider = () => Promise<string>;

// Authentication token provider (set by auth context)
let authGetAccessToken: TokenProvider | null = null;

/**
 * Set the authentication token provider
 * Called by the auth context when user logs in
 */
export function setAuthTokenProvider(getAccessToken: TokenProvider): void {
  authGetAccessToken = getAccessToken;
}

/**
 * Clear the authentication token provider
 * Called by the auth context when user logs out
 */
export function clearAuthTokenProvider(): void {
  authGetAccessToken = null;
}

/**
 * Standard API error format (Sovra convention)
 */
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * API response wrapper
 */
export interface ApiErrorResponse {
  detail: ApiError;
}

/**
 * Create axios instance with default configuration
 */
const client = axios.create({
  // BaseURL will be set dynamically via interceptor
  baseURL: "",
  timeout: 30000, // 30 second timeout
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  // Include credentials (cookies) in cross-origin requests
  // Required for migration session cookie to be sent
  withCredentials: true,
});

/**
 * Request interceptor
 * - Sets baseURL from config
 * - Adds authentication token if available
 */
client.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // In dev mode, use relative paths so requests go through the Vite proxy.
    // In production, set the full base URL from config.
    if (!import.meta.env.DEV) {
      const appConfig = getConfigSync();
      config.baseURL = appConfig.apiBaseUrl;
    }

    // Add auth token if provider is set
    if (authGetAccessToken) {
      try {
        const token = await authGetAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;

          // Debug logging in development
          if (import.meta.env.DEV) {
            console.log("[API Client] Request with auth", {
              url: config.url,
              method: config.method,
              tokenPreview: token.substring(0, 20) + "...",
            });
          }
        }
      } catch (error) {
        console.error("[API Client] Failed to get access token:", error);
        // Continue without token - will likely fail with 401
      }
    } else if (import.meta.env.DEV) {
      console.log("[API Client] Request without auth", {
        url: config.url,
        method: config.method,
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * - Transforms errors to standard format
 * - Handles authentication errors (401)
 */
client.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError<ApiErrorResponse>) => {
    // Handle network errors
    if (!error.response) {
      console.error("[API Client] Network error:", error.message);
      return Promise.reject({
        code: "NETWORK_ERROR",
        message:
          "Unable to connect to the server. Please check your connection.",
        details: { originalError: error.message },
      } as ApiError);
    }

    const { status, data } = error.response;

    // Handle 401 Unauthorized (do not clear session for wrong current password)
    if (status === 401) {
      const detail = data?.detail as { code?: string } | undefined;
      const code =
        detail && typeof detail === "object" ? detail.code : undefined;
      if (code !== "INVALID_PASSWORD") {
        console.warn("[API Client] Unauthorized - clearing session");
        clearAuthTokenProvider();
        // TODO: Trigger auth context to redirect to login
        // window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      }
    }

    // Handle 403 Forbidden
    if (status === 403) {
      console.warn("[API Client] Forbidden - insufficient permissions");
    }

    // Transform to standard error format
    if (data?.detail) {
      return Promise.reject(data.detail);
    }

    // Fallback error format
    const apiError: ApiError = {
      code: `HTTP_${status}`,
      message: getDefaultErrorMessage(status),
      details: { status, originalMessage: error.message },
    };

    return Promise.reject(apiError);
  }
);

/**
 * Get default error message for HTTP status codes
 */
function getDefaultErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return "Invalid request. Please check your input.";
    case 401:
      return "Your session has expired. Please log in again.";
    case 403:
      return "You don't have permission to perform this action.";
    case 404:
      return "The requested resource was not found.";
    case 409:
      return "A conflict occurred. Please try again.";
    case 422:
      return "The request could not be processed. Please check your input.";
    case 429:
      return "Too many requests. Please wait and try again.";
    case 500:
      return "An internal server error occurred. Please try again later.";
    case 502:
    case 503:
    case 504:
      return "The service is temporarily unavailable. Please try again later.";
    default:
      return "An unexpected error occurred. Please try again.";
  }
}

export default client;
